import {
  createSlice,
  createSelector,
  createAsyncThunk,
} from '@reduxjs/toolkit';

import {
  onCollapseOpen as _onCollapseOpen,
  hasError,
  assData,
} from 'src/pages/SalesChannelContentEditor/slice/reduxCommonFunctions';
import {
  defaultStatus,
  generateDefaultState as _generateDefaultState,
} from 'src/pages/SalesChannelContentEditor/slice/reduxCommonFunctions';
import {
  addDraggableCollapse,
  deleteDraggableCollapse,
} from 'src/constrains/DraggableModules/reduxFunctions';
import {
  getCollapseOpenStatus as _getCollapseOpenStatus,
  EnumReducers,
  getState as _getState,
  getGeneralFeaturedProduct as _getGeneralFeaturedProduct,
  getSalesChannel as _getSalesChannel,
  getSalesChannelName as _getSalesChannelName,
  getHasModified as _getHasModified,
  getSubmitStamp as _getSubmitStamp,
  getLoaded as _getLoaded,
  getCollapseValidationMsgDisplay as _getCollapseValidationMsgDisplay,
  getReducer,
} from 'src/pages/SalesChannelContentEditor/slice/selectors';

import {
  EnumCollapseId,
  fixedCollapse,
  generateNewMenu,
} from 'src/pages/SalesChannelContentEditor/SalesChannelMegaMenu/common';
export { EnumCollapseId };
import { msg } from 'src/tools/message';

import { convertDataFromServerToRedux } from 'src/pages/SalesChannelContentEditor/SalesChannelMegaMenu/tools';

import { isCurrentPrimary } from 'src/constrains/SalesChannelMultiLanguageSelect';
import { get } from 'lodash';
import { ActionType } from 'src/types/common/common';
import arrayMove from 'array-move';

import {
  getMegaMenu,
  postMegaMenu,
  getMegaMenuMetaInfo,
} from '@/src/request/salesChannel/content';

export const fetchMegaMenu = createAsyncThunk(
  'fetchSalesChannelContentMegaMenuPage',
  async (data: any) => {
    const { uuid, params = {}, isClone } = data;
    const isPrimary = isCurrentPrimary();
    const response = await getMegaMenu(uuid, params);
    return { response, isPrimary, isClone };
  },
);

export const fetchMegaMenuInfo = createAsyncThunk(
  'fetchSalesChannelContentMegaMenuPageMetaInfo',
  async (data: any) => {
    const { uuid, params = {} } = data;
    //const isPrimary = isCurrentPrimary();
    const response = await getMegaMenuMetaInfo(uuid, params);
    return { response };
  },
);

export const submitMegaMenu = createAsyncThunk(
  'submitSalesChannelContentMegaMenuPage',
  async ({ body, params }: any) => {
    const isPrimary = isCurrentPrimary();
    const response = await postMegaMenu(body, params);
    return { response, isPrimary };
  },
);

const _defaultState = _generateDefaultState(fixedCollapse);

const defaultState = {
  ..._defaultState,
  ...defaultStatus,
  animationList: [],
  navigationList: [],
  contentPageList: [],
};

const salesChannelContentMegaMenuPage = createSlice({
  name: 'salesChannelContentMegaMenuPage',
  initialState: defaultState,
  reducers: {
    onCollapseOpen(state, action) {
      const { idArr = [] } = action.payload; //=>[id:string]
      _onCollapseOpen(state, idArr);
    },
    onErrorChange(state, action) {
      const { id, status } = action.payload;
      state.collapseErrorMsgDisplay[id].hasError = status;
    },
    resetData(state) {
      assData(state, defaultState);
    },
    validation(state) {
      hasError(state, state.collapseErrorMsgDisplay || {});
    },
    onMegaMenuStyleChange(state, action) {
      state.menuStyle = { ...state.menuStyle, ...action.payload };
      state.hasModified = true;
    },
    onMegaMenuTitleChange(state, action) {
      state.hasModified = true;
      const { index, title } = action.payload;
      state.megaMenuList[index].title = title;
    },
    onMegaMenuLinkModuleChange(state, action) {
      state.hasModified = true;
      const { index, linkModule } = action.payload;
      state.megaMenuList[index].linkModule = linkModule;
    },
    onMegaMenuChange(state, action) {
      state.hasModified = true;
      const { type } = action.payload;
      if (type === ActionType.delete) {
        const { index, draggableId } = action.payload;
        state.megaMenuList.splice(index, 1);
        deleteDraggableCollapse(state, draggableId);
      }
      if (type === ActionType.add) {
        const amount = state.megaMenuList.length;
        const newMenu = generateNewMenu(amount + 1);
        const { draggableId } = newMenu;
        state.megaMenuList.push(newMenu);
        addDraggableCollapse(state, draggableId);
      }
      if (type === ActionType.reorder) {
        const { from, to } = action.payload;
        state.megaMenuList = arrayMove(state.megaMenuList, from, to);
      }
      if (type === ActionType.modify) {
        const { index, target, colIndex } = action.payload;
        state.megaMenuList[index]['menuColumns'][colIndex] = {
          ...state.megaMenuList[index]['menuColumns'][colIndex],
          ...target,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMegaMenu.fulfilled, (state, action) => {
      const { response, isClone } = action.payload;
      //  console.log('response', response);

      const _data = convertDataFromServerToRedux(response) || {};

      assData(state, _data);

      if (isClone) state.hasModified = true;
    });
    // builder.addCase(fetchMegaMenu.pending, (state, action) => {
    //   state.loaded = false;
    // });
    // builder.addCase(fetchMegaMenu.rejected, (state, action) => {
    //   state.loaded = true;
    // });
    builder.addCase(fetchMegaMenuInfo.fulfilled, (state, action) => {
      const { response } = action.payload;
      // console.log('response', response);
      const { contentPages, navigationGroups, animations } = response;

      state.animationList = animations || [];
      state.navigationList = navigationGroups || [];
      state.contentPageList = contentPages || [];
    });
    // builder.addCase(fetchMegaMenuInfo.pending, (state, action) => {
    //   state.loaded = false;
    // });
    // builder.addCase(fetchMegaMenuInfo.rejected, (state, action) => {
    //   state.loaded = true;
    // });

    builder.addCase(submitMegaMenu.fulfilled, (state, action) => {
      const { response } = action.payload;
      const _data = convertDataFromServerToRedux(response) || {};
      msg.success('Mega menu has been updated');
      assData(state, { ..._data });
    });
  },
});

export const {
  onCollapseOpen,
  onErrorChange,
  resetData,
  validation,
  onMegaMenuStyleChange,
  onMegaMenuChange,
  onMegaMenuTitleChange,
  onMegaMenuLinkModuleChange,
} = salesChannelContentMegaMenuPage.actions;
export {
  getAnimationList,
  getMegaMenuStyle,
  getMegaMenus,
  getContentPageList,
  getNavigationList,
};

const getAnimationList = () => {
  return createSelector(
    (state: any) => get(state, getReducer(EnumReducers.megaMenu)).animationList,
    (animationList) => {
      //   console.log('footerStyle', footerStyle);
      return animationList;
    },
  );
};

const getContentPageList = () => {
  return createSelector(
    (state: any) =>
      get(state, getReducer(EnumReducers.megaMenu)).contentPageList,
    (contentPageList) => {
      //   console.log('footerStyle', footerStyle);
      return contentPageList;
    },
  );
};

const getNavigationList = () => {
  return createSelector(
    (state: any) =>
      get(state, getReducer(EnumReducers.megaMenu)).navigationList,
    (navigationList) => {
      //   console.log('footerStyle', footerStyle);
      return navigationList;
    },
  );
};

const getMegaMenuStyle = () => {
  return createSelector(
    (state: any) => get(state, getReducer(EnumReducers.megaMenu)).menuStyle,
    (animationList) => {
      //   console.log('footerStyle', footerStyle);
      return animationList;
    },
  );
};

const getMegaMenus = () => {
  return createSelector(
    (state: any) => get(state, getReducer(EnumReducers.megaMenu)).megaMenuList,
    (megaMenuList) => {
      //   console.log('footerStyle', footerStyle);
      return megaMenuList;
    },
  );
};

export const getLoaded = () => {
  return _getLoaded(EnumReducers.megaMenu);
};

export const getSubmitStamp = () => {
  return _getSubmitStamp(EnumReducers.megaMenu);
};

export const getCollapseOpenStatus = (id) => {
  return _getCollapseOpenStatus(EnumReducers.megaMenu, id);
};

export const getCollapseValidationMsgDisplay = (id) => {
  return _getCollapseValidationMsgDisplay(EnumReducers.megaMenu, id);
};

export const getGeneralFeaturedProduct = () => {
  return _getGeneralFeaturedProduct(EnumReducers.megaMenu);
};

export const getState = () => {
  return _getState(EnumReducers.megaMenu);
};

export const getHasModified = () => {
  return _getHasModified(EnumReducers.megaMenu);
};

export const getSalesChannel = () => {
  return _getSalesChannel(EnumReducers.megaMenu);
};

export const getSalesChannelName = () => {
  return _getSalesChannelName(EnumReducers.megaMenu);
};

export default salesChannelContentMegaMenuPage.reducer;
